/** Despacho de acciones de workflow por `execution_kind` (design.md §22.7). */
import type { WorkflowContextAction } from '@/services/tasksService';

export type WorkflowActionPayload = {
  reason?: string;
  membership_id?: string;
  [key: string]: unknown;
};

export type WorkflowActionHandler = (
  action: WorkflowContextAction,
  payload: WorkflowActionPayload,
) => Promise<unknown>;

export type WorkflowActionHandlers = {
  domainCommands: Readonly<Record<string, WorkflowActionHandler>>;
  request: WorkflowActionHandler;
  decision: WorkflowActionHandler;
  withdraw: WorkflowActionHandler;
  resume: WorkflowActionHandler;
  reassign: WorkflowActionHandler;
};

export function workflowActionCode(action: WorkflowContextAction): string {
  return (action.action_code ?? action.code).trim();
}

export function isWorkflowActionDisabled(action: WorkflowContextAction): boolean {
  if (action.disabled === true) return true;
  if (action.enabled === false) return true;
  return false;
}

export function workflowActionDisabledReason(action: WorkflowContextAction): string | null {
  return action.disabled_reason ?? null;
}

export function resolveWorkflowActionHandler(
  action: WorkflowContextAction,
  handlers: WorkflowActionHandlers,
): WorkflowActionHandler | null {
  const code = workflowActionCode(action);
  switch (action.execution_kind) {
    case 'domain_command':
      return Object.prototype.hasOwnProperty.call(handlers.domainCommands, code)
        ? handlers.domainCommands[code]
        : null;
    case 'workflow_request':
      return code === 'request_workflow' && action.workflow_point_code
        ? handlers.request
        : null;
    case 'workflow_decision':
      return action.task_id && (code === 'approve' || code === 'reject')
        ? handlers.decision
        : null;
    case 'workflow_control':
      if (code === 'withdraw_workflow') return handlers.withdraw;
      if (code === 'resume_workflow') return handlers.resume;
      if (code === 'reassign_task' && action.task_id) return handlers.reassign;
      return null;
    default:
      return null;
  }
}

export async function dispatchWorkflowAction(
  action: WorkflowContextAction,
  payload: WorkflowActionPayload,
  handlers: WorkflowActionHandlers,
): Promise<unknown> {
  const code = workflowActionCode(action);
  if (isWorkflowActionDisabled(action)) {
    throw new Error(
      workflowActionDisabledReason(action) ?? `La acción "${action.label || code}" no está disponible`,
    );
  }
  const handler = resolveWorkflowActionHandler(action, handlers);
  if (!handler) {
    throw new Error(`Acción no soportada por el cliente: "${code}"`);
  }
  return handler(action, payload);
}
